import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { BulkDialogContentProps } from "@components/common/faro-table/bulk-dialog/bulk-dialog-types";
import { BulkActionListItem } from "@components/common/faro-table/bulk-dialog/content-list/bulk-action-list-item";
import { TableDataType } from "@components/common/faro-table/faro-table-types";
import { SphereDivider } from "@components/common/sphere-divider";
import { useBulkActionCallback } from "@hooks/table/use-bulk-action-callback";
import { Box, List, SvgIcon, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  isBulkActionProcessingSelector,
  updatingItemsSelector,
} from "@store/table/table-selector";
import { setBulkActionName } from "@store/table/table-slice";
import { getSvgColoredIconCss } from "@styles/common-styles";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { PropsWithChildren, useMemo } from "react";
import { Dialog, DialogVariants, red, XMarkCircleFillIcon } from "@flat-ui";

interface Props<T> {
  /** The content of the dialog */
  dialogContent: BulkDialogContentProps;

  /** Callback when the dialog closes */
  onClose: () => void;

  /** Function to be applied on each selected entity */
  bulkActionCallback: (entity: T) => Promise<unknown>;

  /** Callback function triggers when all bulk action completed successfully */
  onSuccessfulComplete?(): void;

  /** The event name sent for tracking */
  trackingEvent: BulkActionEvents;

  /** Whether to disable the confirm button or not */
  shouldDisableConfirmButton?: boolean;

  /** The entities that are selected */
  selectedEntities: T[];

  /** A key from entity that is unique like ID and it should be in the type of number or string */
  uniqueIdKey: keyof T;

  /** The type of entity for bulk actions */
  tableDataType: TableDataType;

  /**
   * Optional property to change the color of the confirm button.
   * Default blue.
   */
  dialogVariant?: DialogVariants;
}

/** Dialog for showing bulk action confirmation and report */
export function FaroBulkDialog<T>({
  dialogContent,
  onClose,
  bulkActionCallback,
  onSuccessfulComplete,
  trackingEvent,
  children,
  shouldDisableConfirmButton = false,
  selectedEntities,
  uniqueIdKey,
  tableDataType,
  dialogVariant = "info",
}: PropsWithChildren<Props<T>>): JSX.Element {
  const dispatch = useAppDispatch();
  const updatingItems = useAppSelector(updatingItemsSelector);
  const isBulkActionProcessing = useAppSelector(isBulkActionProcessingSelector);

  const { onBulkActionConfirmed } = useBulkActionCallback({
    selectedEntities,
    uniqueIdKey,
    bulkActionCallback,
    onSuccessfulComplete,
    trackingEvent,
  });

  const isConfirmButtonDisabled = useMemo(() => {
    // Disable confirm button if none of the items are allowed to be updated
    const isAllItemsNotAllowed = updatingItems.every(
      (item) => item.status === "not-allowed"
    );
    return shouldDisableConfirmButton || isAllItemsNotAllowed;
  }, [shouldDisableConfirmButton, updatingItems]);

  return (
    <Dialog
      title={
        <>
          {dialogContent.shouldShowFailedIcon ? (
            <SvgIcon
              inheritViewBox
              component={XMarkCircleFillIcon}
              sx={{
                ...getSvgColoredIconCss(red[500]),
                height: "28px",
                width: "28px",
                mr: "12px",
              }}
            />
          ) : null}
          {dialogContent.title}
        </>
      }
      confirmText={capitalizeFirstLetter(dialogContent.confirmButtonText)}
      open={true}
      isConfirmDisabled={isConfirmButtonDisabled}
      onConfirm={onBulkActionConfirmed}
      onCancel={() => {
        dispatch(setBulkActionName(null));
        onClose();
      }}
      showSpinner={isBulkActionProcessing}
      variant={dialogVariant}
    >
      {/* Description of the dialog */}
      <Box
        component="div"
        sx={{
          fontSize: "14px",
          mb: SPACE_ELEMENTS_OF_MODAL,
        }}
      >
        {dialogContent.description}
      </Box>

      {children}

      <SphereDivider sx={{ mb: SPACE_ELEMENTS_OF_MODAL }} />

      <Typography
        sx={{
          fontSize: "14px",
          mb: SPACE_ELEMENTS_OF_MODAL,
        }}
      >
        {dialogContent.selectedEntitiesReport}
      </Typography>

      {/* List of items being updated */}
      <Box component="div" sx={{ maxHeight: "200px", overflow: "auto" }}>
        <List dense sx={{ py: "0px" }}>
          {updatingItems.map((updatingItem) => (
            <BulkActionListItem
              key={updatingItem.id}
              item={updatingItem}
              tableDataType={tableDataType}
            />
          ))}
        </List>
      </Box>
    </Dialog>
  );
}
